/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import ClaimReserveTable from './claim-reserve-table';
import './reserves-tab.scss';
import {
  useReserveTypesQuery,
  useUpsertReserveMutation,
  useReservesQuery,
  getPaginatedNodes,
  ReservePayment,
  useCloseClaimDetailsQuery,
} from '@converge/graphql';
import { useOutletContext, useParams } from 'react-router-dom';
import { useConvergeSnackBar } from '../../../../contexts';
import {
  CircularLoader,
  ConvergeDetailAccordion,
} from '../../../../presentation';

interface ReservesTabProps {
  formatDollarAmount: (amount: number) => string;
  createReserve: () => void;
  handleAddPayment: (reserveId: string) => void;
  loading?: boolean;
  claimUuid: string;
}

interface IClaimDetailsProps {
  vendorsData: Record<string, unknown>;
}

export const ReservesTab: React.FC<ReservesTabProps> = ({
  formatDollarAmount,
  createReserve,
  handleAddPayment,
  loading = false,
  claimUuid,
}) => {
  const [expandedAlae, setExpandedAlae] = useState(true);
  const [expandedUlae, setExpandedUlae] = useState(true);
  const [savingReserveId, setSavingReserveId] = useState<string>(''); // Track which reserve is being saved
  const [reserveTypes, setReserveTypes] = useState<
    Array<{
      uuid: string;
      category?: string;
      reserveType?: string;
    }>
  >([]);
  // Context provides vendor data if needed
  useOutletContext<IClaimDetailsProps>();
  const params = useParams<{ claimUuid: string }>();
  const claimUuidFromParams = params.claimUuid;

  // Vendor data available from context
  // Define form field types
  type ReserveFormValues = {
    [key: string]: {
      remainingReserve: number;
      paymentTotal: number;
      budget: number;
    };
  };

  const { notify } = useConvergeSnackBar();

  // Set up form to manage reserve values
  const formMethods = useForm<ReserveFormValues>({
    defaultValues: {},
  });

  // Fetch reserve types
  const { data: reserveTypesData, loading: reserveTypesLoading } =
    useReserveTypesQuery({
      variables: {
        reserveTypeFilterInput: {},
      },
    });

  // Fetch actual reserves data using the claim UUID
  const { data: reservesData, loading: reservesLoading } = useReservesQuery({
    claimUuid: claimUuidFromParams,
  });

  // Set up upsert reserve mutation
  const { upsertReserve, loading: upsertReserveLoading } =
    useUpsertReserveMutation();

  const { refetch: refetchCloseClaimDetails } = useCloseClaimDetailsQuery({
    claimUuid: claimUuidFromParams || '',
  });

  // Store reserve types data in state when available and filter by categories
  useEffect(() => {
    if (reserveTypesData?.reserveTypes?.edges && reserveTypes?.length === 0) {
      const typesData = getPaginatedNodes(reserveTypesData?.reserveTypes).map(
        (node) => ({
          uuid: node.uuid,
          category: node.name || undefined,
          reserveType: node.name || undefined, // Using category as reserveType for now
        })
      );

      setReserveTypes(typesData);
      // Reserve types now available in state
    }
  }, [reserveTypesData, reserveTypes?.length]);

  // Add CSS class to handle accordion styling
  React.useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .accordion-summary .MuiAccordionSummary-content {
        margin-left: 8px !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle reserve form submission with the correct reserve type UUID
  const handleReserveSubmit = (
    reserveTypeUuid: string,
    remainingReserve: number,
    reserveUuid?: string
  ) => {
    // Submit the reserve data to the API
    // Use either the provided claimUuid prop or the one from URL params
    const claimIdToUse = claimUuid || claimUuidFromParams;

    if (!claimIdToUse) {
      console.error('No claim UUID available for upsertReserve mutation');
      return Promise.reject('No claim UUID available');
    }

    // Set the reserve ID that's being saved
    setSavingReserveId(reserveTypeUuid);

    return upsertReserve({
      variables: {
        reserveInput: {
          claimUuid: claimIdToUse,
          reserveTypeUuid,
          remainingReserve: Number(remainingReserve), // Ensure it's a number
          reserveUuid,
        },
      },
    })
      .then((result) => {
        // Clear the saving state when complete
        setSavingReserveId('');
        // Refetch close claim details to update validation status
        refetchCloseClaimDetails();
        notify(`Reserve amount has been updated `, {
          variant: 'success',
        });
        return result;
      })
      .catch((error) => {
        console.error('Error updating reserve:', error);
        notify(`Failed to update reserve`, {
          variant: 'error',
        });
        // Also clear saving state on error
        setSavingReserveId('');
        throw error;
      });
  };

  // Filter reserve types by categories and limit to first of each
  const alaeAndLossReserveTypes = useMemo(() => {
    // Find first ALAE and first LOSS reserve types
    const firstAlae = reserveTypes.find((type) => type.category === 'ALAE');
    const firstLoss = reserveTypes.find((type) => type.category === 'LOSS');

    // Return as array, filtering out undefined values
    return [firstAlae, firstLoss].filter(Boolean) as Array<{
      uuid: string;
      category?: string;
      reserveType?: string;
    }>;
  }, [reserveTypes]);

  const ulaeReserveTypes = useMemo(() => {
    // Find only the first ULAE reserve type
    const firstUlae = reserveTypes.find((type) => type.category === 'ULAE');

    // Return as array, filtering out undefined values
    return firstUlae ? [firstUlae] : [];
  }, [reserveTypes]);

  // Map reserve types to their data from the API
  const mapReserveTypeToData = useMemo(() => {
    if (!reservesData?.reserves?.edges) return {};

    const map: Record<string, any> = {};
    reservesData.reserves.edges.forEach((edge) => {
      if (edge.node?.reserveType?.uuid) {
        map[edge.node.reserveType.uuid] = edge.node;
      }
    });

    return map;
  }, [reservesData]);

  // Create reserves data from reserve types and API data
  const alaeAndLossReserves = useMemo(() => {
    // Create a unified set of reserve type UUIDs from both predefined types and API data
    const allReserveTypeUuids = new Set([
      ...alaeAndLossReserveTypes.map((type) => type.uuid),
      ...Object.values(mapReserveTypeToData)
        .filter(
          (data: any) =>
            data?.reserveType?.category === 'ALAE' ||
            data?.reserveType?.category === 'LOSS'
        )
        .map((data: any) => data.reserveType?.uuid)
        .filter(Boolean),
    ]);

    return Array.from(allReserveTypeUuids).map((reserveTypeUuid) => {
      // Find the predefined reserve type
      const predefinedType = alaeAndLossReserveTypes.find(
        (type) => type.uuid === reserveTypeUuid
      );
      // Find the API data for this reserve type
      const reserveData = mapReserveTypeToData[reserveTypeUuid];

      const reserveUuid = reserveData?.uuid || '';
      const remainingReserve = reserveData?.remainingReserve || 0;
      const paymentTotal = reserveData?.paymentTotal || 0;
      const reservePayments = getPaginatedNodes(
        reserveData?.payments
      ) as ReservePayment[];
      const totalIncurred =
        reserveData?.totalIncurred || remainingReserve + paymentTotal;

      // Determine name and category - prefer API data, fallback to predefined type
      const category =
        reserveData?.reserveType?.category || predefinedType?.category || '';
      const name =
        reserveData?.reserveType?.category ||
        predefinedType?.reserveType ||
        predefinedType?.category ||
        '';

      // Set form values from real data
      formMethods.setValue(
        `${reserveTypeUuid}.remainingReserve` as `${string}.remainingReserve`,
        remainingReserve
      );
      formMethods.setValue(
        `${reserveTypeUuid}.paymentTotal` as `${string}.paymentTotal`,
        paymentTotal
      );
      formMethods.setValue(
        `${reserveTypeUuid}.budget` as `${string}.budget`,
        totalIncurred
      );

      return {
        uuid: reserveUuid,
        reserveType: {
          name: name,
          category: category,
          uuid: reserveTypeUuid,
        },
        budget: totalIncurred,
        paymentTotal: paymentTotal,
        reservePayments: reservePayments,
        budgetType: 'Fixed',
        budgetMin: null,
        budgetMax: null,
        remainingReserve: remainingReserve,
        coverage: reserveData?.coverages?.edges?.[0]?.node || {
          uuid: 'cov-1',
          name: `${category || 'Default'} Coverage`,
          quote: { limit: 100000, retention: 5000 },
        },
        vendor: { node: { uuid: 'vendor-1', name: 'Provider LLC' } },
        service: {
          uuid: 'service-1',
          name: `${category || 'Default'} Service`,
        },
        payments: reserveData?.payments || { edges: [] },
      };
    });
  }, [alaeAndLossReserveTypes, mapReserveTypeToData, formMethods]);

  const ulaeReserves = useMemo(() => {
    // Create a unified set of reserve type UUIDs from both predefined types and API data
    const allReserveTypeUuids = new Set([
      ...ulaeReserveTypes.map((type) => type.uuid),
      ...Object.values(mapReserveTypeToData)
        .filter((data: any) => data?.reserveType?.category === 'ULAE')
        .map((data: any) => data.reserveType?.uuid)
        .filter(Boolean),
    ]);

    return Array.from(allReserveTypeUuids).map((reserveTypeUuid) => {
      // Find the predefined reserve type
      const predefinedType = ulaeReserveTypes.find(
        (type) => type.uuid === reserveTypeUuid
      );
      // Find the API data for this reserve type
      const reserveData = mapReserveTypeToData[reserveTypeUuid];

      // Use real data if available, otherwise use defaults
      const reserveUuid = reserveData?.uuid || '';
      const remainingReserve = reserveData?.remainingReserve || 0;
      const paymentTotal = reserveData?.paymentTotal || 0;
      const reservePayments = getPaginatedNodes(reserveData?.payments);
      const totalIncurred =
        reserveData?.totalIncurred || remainingReserve + paymentTotal;

      // Determine name and category - prefer API data, fallback to predefined type
      const category =
        reserveData?.reserveType?.category ||
        predefinedType?.category ||
        'ULAE';
      const name =
        reserveData?.reserveType?.category ||
        predefinedType?.reserveType ||
        predefinedType?.category ||
        'ULAE';

      // Set form values from real data
      formMethods.setValue(
        `${reserveTypeUuid}.remainingReserve` as `${string}.remainingReserve`,
        remainingReserve
      );
      formMethods.setValue(
        `${reserveTypeUuid}.paymentTotal` as `${string}.paymentTotal`,
        paymentTotal
      );
      formMethods.setValue(
        `${reserveTypeUuid}.budget` as `${string}.budget`,
        totalIncurred
      );

      return {
        uuid: reserveUuid,
        reserveType: {
          name: name,
          category: category,
          uuid: reserveTypeUuid,
        },
        budget: totalIncurred,
        paymentTotal: paymentTotal,
        reservePayments: reservePayments as ReservePayment[],
        budgetType: 'Fixed',
        budgetMin: null,
        budgetMax: null,
        remainingReserve: remainingReserve,
        coverage: reserveData?.coverages?.edges?.[0]?.node || {
          uuid: 'cov-1',
          name: 'ULAE Coverage',
          quote: { limit: 50000, retention: 1000 },
        },
        vendor: { node: { uuid: 'vendor-1', name: 'Provider LLC' } },
        service: { uuid: 'service-1', name: 'ULAE Service' },
        payments: reserveData?.payments || { edges: [] },
      };
    });
  }, [ulaeReserveTypes, mapReserveTypeToData, formMethods]);

  // Custom handler for saving changes
  const handleSaveChanges = (
    reserveId: string,
    updatedData: { remainingReserve?: number },
    reserveUuid?: string,
    onSaveComplete?: () => void
  ) => {
    // Update form values
    if (updatedData.remainingReserve !== undefined) {
      formMethods.setValue(
        `${reserveId}.remainingReserve` as `${string}.remainingReserve`,
        updatedData.remainingReserve
      );

      // Call the API to update the reserve
      // Ensure remainingReserve is converted to a number
      handleReserveSubmit(
        reserveId,
        Number(updatedData.remainingReserve),
        reserveUuid || ''
      )
        .then(() => {
          // Call the callback when save is complete
          if (onSaveComplete) {
            onSaveComplete();
          }
        })
        .catch((error) => {
          // Handle error case - still call callback to reset UI state
          console.error('Save failed:', error);
          if (onSaveComplete) {
            onSaveComplete();
          }
        });
    }
  };
  const sharedPadding = { paddingLeft: '20px', paddingRight: '20px' };

  return (
    <FormProvider {...formMethods}>
      {reserveTypesLoading || reservesLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            margin: 'auto',
            my: 16,
          }}
        >
          <CircularLoader className="circular-loader-style" />
        </Box>
      ) : (
        <Box className="reserves-tab" sx={{ mt: 6, width: '86vw !important' }}>
          {/* ALAE AND LOSS Accordion */}
          <ConvergeDetailAccordion
            title="ALAE AND LOSS"
            expanded={expandedAlae}
            onChange={(event, isExpanded) => setExpandedAlae(isExpanded)}
            className="accordion-width"
            detailsClassName="accordion-details-style"
            testId="alae-and-loss-accordion"
          >
            <Box sx={sharedPadding}>
              {alaeAndLossReserves.length > 0 ? (
                <ClaimReserveTable
                  reserves={alaeAndLossReserves}
                  formatDollarAmount={formatDollarAmount}
                  handleAddPayment={handleAddPayment}
                  onSaveChanges={handleSaveChanges}
                  savingReserveId={savingReserveId}
                  isLoading={upsertReserveLoading}
                />
              ) : (
                <Typography className="no-reserves-message">
                  No ALAE AND LOSS Reserves
                </Typography>
              )}
            </Box>
          </ConvergeDetailAccordion>

          <Box sx={{ paddingTop: '36px' }}></Box>
          {/* ULAE Accordion */}
          <ConvergeDetailAccordion
            title="ULAE"
            expanded={expandedUlae}
            onChange={(event, isExpanded) => setExpandedUlae(isExpanded)}
            className="accordion-width"
            detailsClassName="accordion-details-style"
            testId="ulae-accordion"
          >
            <Box sx={sharedPadding}>
              {ulaeReserves.length > 0 ? (
                <ClaimReserveTable
                  isUlaeReserve
                  reserves={ulaeReserves}
                  formatDollarAmount={formatDollarAmount}
                  handleAddPayment={handleAddPayment}
                  onSaveChanges={handleSaveChanges}
                  savingReserveId={savingReserveId}
                  isLoading={upsertReserveLoading}
                />
              ) : (
                <Typography className="no-reserves-message">
                  No ULAE Reserves
                </Typography>
              )}
            </Box>
          </ConvergeDetailAccordion>
        </Box>
      )}
    </FormProvider>
  );
};
